import { redirect } from "@tanstack/react-router";
import { createMiddleware, createServerOnlyFn } from "@tanstack/react-start";
import { z } from "zod";

import { db } from "@/db";
import type { Session, User } from "@/lib/auth";
import { auth } from "@/lib/auth";
import { ForbiddenError, InsufficientCreditsError } from "@/lib/errors";
import { $getUserSession } from "@/server/actions/session";
import { isCreditsExempt } from "@/server/billing/credit-exemption";
import {
	ThreadController,
	MessageController,
	BillingController,
	ConversationStepController,
	ContentBlockController,
	ContentController,
	CourseController,
	FileController,
	SubscriptionController,
} from "@/server/controllers";
import { s3Storage } from "@/server/storage/s3";

export const createAuthMiddlewareContext = createServerOnlyFn((session: Session, user: User) => {
	return {
		session,
		user,
		db,
		auth,
		controllers: {
			contentBlockController: new ContentBlockController(db, user.id),
			courseController: new CourseController(db, user.id),
			fileController: new FileController(db, user.id, s3Storage),
			threadController: new ThreadController(db, user.id),
			messageController: new MessageController(db, user.id),
			billingController: new BillingController(db, user.id),
			contentController: new ContentController(db, user.id),
			conversationStepController: new ConversationStepController(db, user.id),
			subscriptionController: new SubscriptionController(db, user.id),
		},
		// durableObject,
	};
});

export const createUnauthMiddlewareContext = createServerOnlyFn(
	(session: Session | null, user: User | null) => {
		const userId = user?.id;
		return {
			session,
			user,
			controllers: {
				fileController: new FileController(db, userId, s3Storage),
				threadController: new ThreadController(db, userId),
				messageController: new MessageController(db, userId),
				contentController: new ContentController(db, userId),
			},
		};
	},
);

export const unauthedMiddleware = createMiddleware({
	type: "function",
}).server(async ({ next }) => {
	const sessionResponse = await $getUserSession();

	return next({
		context: createUnauthMiddlewareContext(
			sessionResponse?.session || null,
			sessionResponse.user || null,
		),
	});
});

export const authMiddleware = createMiddleware({
	type: "function",
}).server(async ({ next, context }) => {
	const sessionResponse = await $getUserSession();

	if (!sessionResponse.session || !sessionResponse.user) {
		throw redirect({ to: "/sign-in" });
	}

	const { session, user } = sessionResponse;

	return next({
		context: createAuthMiddlewareContext(session, user),
	});
});

export const adminMiddleware = createMiddleware({
	type: "function",
}).server(async ({ next }) => {
	const sessionResponse = await $getUserSession();

	if (!sessionResponse.session || !sessionResponse.user) {
		throw redirect({ to: "/sign-in" });
	}

	const { session, user } = sessionResponse;

	if (user.role !== "admin" && user.role !== "superadmin") {
		throw new ForbiddenError("Forbidden: Admin access required");
	}

	return next({
		context: createAuthMiddlewareContext(session, user),
	});
});

/**
 * Function middleware that throws when the user has no credits left.
 * Must be chained after `authMiddleware` so `context.controllers` exists.
 */
export const creditsGuardFnMiddleware = createMiddleware({
	type: "function",
})
	.middleware([authMiddleware])
	.server(async ({ next, context }) => {
		if (isCreditsExempt(context.user.role)) {
			return next({ context });
		}

		const balance = await context.controllers.billingController.getBalance();

		if (balance <= 0) {
			throw new InsufficientCreditsError();
		}

		return next({ context });
	});

/**
 * Auth middleware for API request handlers.
 * Uses better-auth to verify session from request headers.
 */
export const authRequestMiddleware = createMiddleware({
	type: "request",
}).server(async ({ next, request }) => {
	const sessionResponse = await auth.api.getSession({
		headers: request.headers,
	});

	if (!sessionResponse?.session || !sessionResponse?.user) {
		return Response.json({ error: "Unauthorized", success: false }, { status: 401 });
	}

	const { session, user } = sessionResponse;

	return next({
		context: createAuthMiddlewareContext(session, user),
	});
});

/**
 * Create a middleware to validate the request data using Zod schema.
 * @param schema - The Zod schema to validate the request data.
 * @returns A middleware function that validates the request data using the Zod schema.
 */
export const createDataValidator = <T extends z.ZodType>(schema: T) =>
	createMiddleware({
		type: "request",
	}).server(async ({ next, request }) => {
		try {
			const body = await request.json();
			const data = schema.parse(body);

			return next({
				context: { data },
			});
		} catch (error) {
			const message =
				error instanceof z.ZodError
					? z.prettifyError(error)
					: error instanceof Error
						? error.message
						: "Invalid Request Data";
			return Response.json({ error: message, success: false }, { status: 400 });
		}
	});

/**
 * Create a middleware to validate the url params using Zod schema.
 * @param schema - The Zod schema to validate the url params.
 * @returns A middleware function that validates the url params using the Zod schema.
 */
export const createParamsValidator = <T extends z.ZodType>(schema: T) =>
	createMiddleware({
		type: "request",
	}).server(async ({ next, request }) => {
		try {
			const url = new URL(request.url);
			const params = Object.fromEntries(url.searchParams);
			const data = schema.parse(params);

			return next({
				context: { data },
			});
		} catch (error) {
			const message =
				error instanceof z.ZodError
					? z.prettifyError(error)
					: error instanceof Error
						? error.message
						: "Invalid Request Parameters";
			return Response.json({ error: message, success: false }, { status: 400 });
		}
	});

/**
 * Request middleware that rejects with 402 when the user has no credits left.
 * Must be chained after `authRequestMiddleware` so `context.controllers` exists.
 */
export const creditsGuardMiddleware = createMiddleware({
	type: "request",
})
	.middleware([authRequestMiddleware])
	.server(async ({ next, context, request }) => {
		if (isCreditsExempt(context.user.role)) {
			return next({ context });
		}

		const balance = await context.controllers.billingController.getBalance();

		if (balance <= 0) {
			return Response.json({ error: "INSUFFICIENT_CREDITS", balance }, { status: 402 });
		}

		return next({ context });
	});
